import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UnifiedBlock } from '../../services/blockManager';
import './BlockEditor.css';

interface TriggerInfo {
  type: 'command' | 'markdown-link';
  blockId: string;
  position: { top: number; left: number };
  context?: {
    searchQuery?: string;
    startPosition?: number;
  };
}

export interface BlockItemProps {
  block: UnifiedBlock;
  index: number;
  isLast: boolean;
  isFocused: boolean;
  onFocus: (blockId: string, cursorPosition?: number) => void;
  onUpdateContent: (blockId: string, content: string) => void;
  onSplitBlock: (blockId: string, cursorPosition: number) => void;
  onMergeBlock: (currentBlockId: string, previousBlockId: string) => void;
  onNavigateUp: (currentBlockId: string) => void;
  onNavigateDown: (currentBlockId: string) => void;
  onCommandTrigger?: (blockId: string, position: { top: number; left: number }) => void;
  onTrigger?: (triggerInfo: TriggerInfo) => void;
  fullBlockMode?: boolean;
  onInsertBlockBelow?: (blockId: string) => void;
  onDeleteBlock?: (blockId: string) => void;
  onImageClick?: (imageUrl: string, metadata?: { originalName?: string; width?: number; height?: number }) => void;
  onTranslateBlock?: (blockId: string) => void;
}

const SyncStatusIndicator: React.FC<{ status?: 'syncing' | 'synced' | 'error' }> = ({ status }) => {
  if (!status || status === 'synced') {
    return null;
  }

  const indicatorMap = {
    syncing: {
      icon: '⏳',
      title: 'Saving...',
      className: 'sync-indicator syncing',
    },
    error: {
      icon: '⚠️',
      title: 'Failed to save. Changes may be lost.',
      className: 'sync-indicator error',
    },
  };

  const indicator = indicatorMap[status];

  if (!indicator) {
    return null;
  }

  return (
    <div className={indicator.className} title={indicator.title}>
      {indicator.icon}
    </div>
  );
};

const BlockItem: React.FC<BlockItemProps> = ({
  block,
  index,
  isLast,
  isFocused,
  onFocus,
  onUpdateContent,
  onSplitBlock,
  onMergeBlock,
  onNavigateUp,
  onNavigateDown,
  onCommandTrigger,
  onTrigger,
  fullBlockMode = false,
  onInsertBlockBelow,
  onDeleteBlock,
  onImageClick,
  onTranslateBlock
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Auto-enter edit mode when block is focused (skip image blocks)
  React.useEffect(() => {
    if (block.type === 'image') {
      // Image blocks should never be in edit mode
      if (isEditing) {
        setIsEditing(false);
      }
      return;
    }
    
    if (isFocused && !isEditing) {
      setIsEditing(true);
    } else if (!isFocused && isEditing) {
      setIsEditing(false);
    }
  }, [isFocused, isEditing, block.type]);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  const handleClick = (e: React.MouseEvent) => {
    // Don't enter edit mode for image blocks
    if (block.type === 'image') {
      return;
    }

    setIsEditing(true);

    // TODO: implement the cursor positioning properly

    // Simple cursor positioning - place at end for now
    onFocus(block.block_id, block.content.length);
  };

  const handleHabitLinkClick = (e: React.MouseEvent, habitId: number) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/habits/${habitId}`);
  };

  const handleNoteLinkClick = (e: React.MouseEvent, noteId: string) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/notes/${noteId}`);
  };

  const handleMarkdownLinkClick = (e: React.MouseEvent, noteIdentifier: string) => {
    e.preventDefault();
    e.stopPropagation();
    // Navigate using the note identifier (could be note_id or title)
    navigate(`/notes/${noteIdentifier}`);
  };

  const handleUrlClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    e.stopPropagation();
    // Open URL in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleNavigateToBlock = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(false);
    navigate(`/block/${block.block_id}`);
  };

  const handleDeleteBlock = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(false);
    
    if (onDeleteBlock && window.confirm('Delete this block?')) {
      onDeleteBlock(block.block_id);
    }
  };

  const handleTranslateBlock = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(false);
    
    if (onTranslateBlock) {
      onTranslateBlock(block.block_id);
    }
  };

  const handleBlur = async (e: React.FocusEvent) => {
    // Use innerText to preserve newlines, fallback to textContent
    const element = e.target as HTMLElement;
    const newContent = element.innerText || element.textContent || '';
    if (newContent !== block.content) {
      onUpdateContent(block.block_id, newContent);
    }
  };

  const handleInput = (e: React.FormEvent) => {
    // Use innerText to preserve newlines, fallback to textContent
    const element = e.target as HTMLElement;
    const currentContent = element.innerText || element.textContent || '';
    const selection = window.getSelection();
    
    if (selection && selection.rangeCount > 0 && contentRef.current) {
      const range = selection.getRangeAt(0);
      const cursorPosition = range.startOffset;
      
      // Check for command trigger - look for '/' at the beginning or after whitespace
      if (onCommandTrigger && currentContent.length > 0 && currentContent[cursorPosition - 1] === '/') {
        const beforeSlash = currentContent.substring(0, cursorPosition - 1);
        const isAtStart = beforeSlash.trim() === '';
        
        if (isAtStart) {
          const rect = contentRef.current.getBoundingClientRect();
          // Position the command palette with better placement
          const viewportWidth = window.innerWidth;
          const paletteWidth = 250; // Approximate width of command palette
          
          let leftPosition = rect.left + window.scrollX + 20; // Add some offset from block edge
          
          // Adjust if it would go off-screen on the right
          if (leftPosition + paletteWidth > viewportWidth) {
            leftPosition = viewportWidth - paletteWidth - 20;
          }
          
          // Ensure it doesn't go off-screen on the left
          if (leftPosition < 20) {
            leftPosition = 20;
          }
          
          onCommandTrigger(block.block_id, {
            top: rect.bottom + window.scrollY + 2,
            left: leftPosition
          });
        }
      }
      
      // NEW: Check for markdown link trigger - look for [[ pattern
      if (onTrigger) {
        const beforeCursor = currentContent.substring(0, cursorPosition);
        const markdownLinkMatch = beforeCursor.match(/\[\[([^\]]*?)$/);
        
        if (markdownLinkMatch) {
          const searchQuery = markdownLinkMatch[1];
          const startPosition = cursorPosition - markdownLinkMatch[0].length;
          
          const rect = contentRef.current.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const selectorWidth = 320; // Approximate width of note selector
          
          let leftPosition = rect.left + window.scrollX + 20;
          
          // Adjust if it would go off-screen on the right
          if (leftPosition + selectorWidth > viewportWidth) {
            leftPosition = viewportWidth - selectorWidth - 20;
          }
          
          // Ensure it doesn't go off-screen on the left
          if (leftPosition < 20) {
            leftPosition = 20;
          }
          
          onTrigger({
            type: 'markdown-link',
            blockId: block.block_id,
            position: {
              top: rect.bottom + window.scrollY + 2,
              left: leftPosition
            },
            context: {
              searchQuery,
              startPosition
            }
          });
        }
      }
    }
  };

  // Helper function to render content with markdown links and URLs
  const renderContentWithLinks = (content: string, properties?: Record<string, any> | null) => {
    // First, process markdown links and URLs together
    // We need to track both patterns and process them in order
    
    const parts: (string | React.ReactNode)[] = [];
    let keyCounter = 0;
    
    // Pattern for markdown links: [[...]]
    const markdownLinkPattern = /\[\[([^\]]+)\]\]/g;
    // Pattern for URLs: http://, https://, or www. (with domain)
    // Matches URLs with various characters including trailing slashes, query params, etc.
    const urlPattern = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}[^\s<>"']*)/g;
    
    // Collect all matches with their positions
    interface Match {
      index: number;
      length: number;
      type: 'markdown' | 'url';
      content: string;
      linkText?: string;
    }
    
    const matches: Match[] = [];
    
    // Find markdown links
    let markdownMatch: RegExpExecArray | null;
    while ((markdownMatch = markdownLinkPattern.exec(content)) !== null) {
      matches.push({
        index: markdownMatch.index,
        length: markdownMatch[0].length,
        type: 'markdown',
        content: markdownMatch[0],
        linkText: markdownMatch[1]
      });
    }
    
    // Find URLs (but exclude those already inside markdown links)
    urlPattern.lastIndex = 0;
    let urlMatch: RegExpExecArray | null;
    while ((urlMatch = urlPattern.exec(content)) !== null) {
      const urlIndex = urlMatch.index;
      const urlLength = urlMatch[0].length;
      const urlContent = urlMatch[0];
      
      // Check if this URL is inside a markdown link
      const isInsideMarkdownLink = matches.some(m => 
        m.type === 'markdown' && 
        urlIndex >= m.index && 
        urlIndex < m.index + m.length
      );
      
      if (!isInsideMarkdownLink) {
        matches.push({
          index: urlIndex,
          length: urlLength,
          type: 'url',
          content: urlContent
        });
      }
    }
    
    // Sort matches by position
    matches.sort((a, b) => a.index - b.index);
    
    // Remove overlapping matches (prioritize markdown links over URLs)
    const filteredMatches: Match[] = [];
    for (let i = 0; i < matches.length; i++) {
      const current = matches[i];
      let overlaps = false;
      
      for (let j = 0; j < filteredMatches.length; j++) {
        const existing = filteredMatches[j];
        // Check if current overlaps with existing
        if (!(current.index + current.length <= existing.index || 
              current.index >= existing.index + existing.length)) {
          overlaps = true;
          // If current is markdown and existing is url, replace it
          if (current.type === 'markdown' && existing.type === 'url') {
            filteredMatches[j] = current;
          }
          break;
        }
      }
      
      if (!overlaps) {
        filteredMatches.push(current);
      }
    }
    
    // Build the parts array
    let lastIndex = 0;
    
    for (const match of filteredMatches) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }
      
      if (match.type === 'markdown') {
        // Render markdown link
        const linkText = match.linkText || '';
        const markdownLinks = properties?.markdownLinks || {};
        const linkData = markdownLinks[linkText];
        
        if (linkData) {
          parts.push(
            <span
              key={`md-${keyCounter++}`}
              className="markdown-note-link"
              onClick={(e) => handleMarkdownLinkClick(e, linkData.noteId)}
              title={`Open note: ${linkData.noteTitle}`}
            >
              {linkText}
            </span>
          );
        } else {
          parts.push(
            <span
              key={`md-${keyCounter++}`}
              className="markdown-note-link"
              onClick={(e) => handleMarkdownLinkClick(e, linkText)}
              title={`Open note: ${linkText}`}
            >
              {linkText}
            </span>
          );
        }
      } else if (match.type === 'url') {
        // Render URL link
        let url = match.content;
        // Add https:// if it starts with www.
        if (url.startsWith('www.')) {
          url = 'https://' + url;
        }
        
        parts.push(
          <a
            key={`url-${keyCounter++}`}
            href={url}
            className="block-url-link"
            onClick={(e) => handleUrlClick(e, url)}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${url}`}
          >
            {match.content}
          </a>
        );
      }
      
      lastIndex = match.index + match.length;
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }
    
    // Return parts if we have any matches or if content was split, otherwise return original content
    return parts.length > 0 ? parts : content;
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    // Skip keyboard handling for image blocks
    if (block.type === 'image') {
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      // In full block mode, allow default behavior (newline)
      if (fullBlockMode) {
        return; // Let Enter key create a newline
      }
      
      e.preventDefault();
      
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      
      const range = selection.getRangeAt(0);
      const cursorPosition = range.startOffset;
      
      // Update content before splitting
      const element = e.target as HTMLElement;
      const currentContent = element.innerText || element.textContent || '';
      if (currentContent !== block.content) {
        onUpdateContent(block.block_id, currentContent);
      }
      
      onSplitBlock(block.block_id, cursorPosition);
    } else if (e.key === 'Backspace' && index > 0) {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      
      const range = selection.getRangeAt(0);
      const cursorPosition = range.startOffset;
      const element = e.target as HTMLElement;
      const currentContent = element.innerText || element.textContent || '';
      
      // Empty block deletion or beginning merge
      if (currentContent === '' || cursorPosition === 0) {
        e.preventDefault();
        
        // Update content before merging
        if (currentContent !== block.content) {
          onUpdateContent(block.block_id, currentContent);
        }
        
        onMergeBlock(block.block_id, ''); // Parent will determine previous block
      }
    } 
    else if (e.key === 'ArrowUp' && index > 0) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (range.startOffset === 0) {
          onNavigateUp(block.block_id);
        }
      }
    } else if (e.key === 'ArrowDown' && !isLast) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const element = e.target as HTMLElement;
        const currentContent = element.innerText || element.textContent || '';
        if (range.startOffset === currentContent.length) {
          onNavigateDown(block.block_id);
        }
      }
    }
  };

  const renderContent = () => {
    const contentToRender = isEditing 
      ? block.content || '\u00A0'
      : renderContentWithLinks(block.content || '', block.properties);
    
    const baseProps = {
      contentEditable: isEditing,
      suppressContentEditableWarning: true,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      onInput: handleInput,
      onClick: !isEditing ? handleClick : undefined,
      children: contentToRender
    };

    if (block.type === 'heading') {
      const level = block.level || 1;
      const className = `block-heading heading-${level}`;
      
      switch (level) {
        case 1: return <h1 ref={contentRef as React.RefObject<HTMLHeadingElement>} className={className} {...baseProps} />;
        case 2: return <h2 ref={contentRef as React.RefObject<HTMLHeadingElement>} className={className} {...baseProps} />;
        case 3: return <h3 ref={contentRef as React.RefObject<HTMLHeadingElement>} className={className} {...baseProps} />;
        case 4: return <h4 ref={contentRef as React.RefObject<HTMLHeadingElement>} className={className} {...baseProps} />;
        case 5: return <h5 ref={contentRef as React.RefObject<HTMLHeadingElement>} className={className} {...baseProps} />;
        case 6: return <h6 ref={contentRef as React.RefObject<HTMLHeadingElement>} className={className} {...baseProps} />;
        default: return <h1 ref={contentRef as React.RefObject<HTMLHeadingElement>} className={className} {...baseProps} />;
      }
    }

    if (block.type === 'list-item') {
      return (
        <div className="block-list-item">
          <span className="list-bullet">•</span>
          <div ref={contentRef} className="list-content" {...baseProps} />
        </div>
      );
    }

    if (block.type === 'habit-link') {
      // Parse habit data from content
      let habitData: { id: number; name: string; icon?: string; category?: string } | null = null;
      try {
        habitData = JSON.parse(block.content);
      } catch (e) {
        // Fallback if content is not valid JSON
      }

      if (habitData && !isEditing) {
        return (
          <div 
            className="block-habit-link" 
            onClick={(e) => handleHabitLinkClick(e, habitData!.id)}
            title={`Click to view ${habitData.name} details`}
          >
            <span className="habit-link-icon">{habitData.icon || '📋'}</span>
            <span className="habit-link-name">{habitData.name}</span>
          </div>
        );
      } else {
        // When editing, show as regular text
        return <div ref={contentRef} className="block-paragraph" {...baseProps} />;
      }
    }

    if (block.type === 'note-link') {
      // Parse note data from content
      let noteData: { note_id: string; title: string } | null = null;
      try {
        noteData = JSON.parse(block.content);
      } catch (e) {
        // Fallback if content is not valid JSON
      }

      if (noteData && !isEditing) {
        return (
          <div 
            className="block-note-link" 
            onClick={(e) => handleNoteLinkClick(e, noteData!.note_id)}
            title={`Click to open ${noteData.title} note`}
          >
            <span className="note-link-icon">📝</span>
            <span className="note-link-title">{noteData.title}</span>
          </div>
        );
      } else {
        // When editing, show as regular text
        return <div ref={contentRef} className="block-paragraph" {...baseProps} />;
      }
    }

    if (block.type === 'image') {
      // For image blocks, content is the image URL
      const imageUrl = block.content || block.text_value || '';
      
      // Try to parse metadata from properties
      let imageMetadata: { originalName?: string; width?: number; height?: number } | undefined;
      try {
        const properties = block.properties;
        if (properties?.imageMetadata) {
          imageMetadata = properties.imageMetadata;
        }
      } catch (e) {
        // Ignore parsing errors
      }

      if (imageUrl && !isEditing) {
        return (
          <div className="block-image">
            <img
              src={imageUrl}
              alt={imageMetadata?.originalName || 'Image'}
              className="block-image-thumbnail"
              onClick={() => {
                if (onImageClick) {
                  onImageClick(imageUrl, imageMetadata);
                }
              }}
              onError={(e) => {
                // Show error state if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const errorDiv = document.createElement('div');
                errorDiv.className = 'block-image-error';
                errorDiv.textContent = 'Failed to load image';
                target.parentElement?.appendChild(errorDiv);
              }}
              title="Click to view full size"
            />
          </div>
        );
      } else {
        // When editing or no URL, show placeholder
        return (
          <div ref={contentRef} className="block-image-placeholder">
            {imageUrl ? 'Loading image...' : 'No image URL'}
          </div>
        );
      }
    }

    // Default paragraph
    return <div ref={contentRef} className="block-paragraph" {...baseProps} />;
  };

  // Check if block has sub-blocks
  const hasSubBlocks = block.blocks && Array.isArray(block.blocks) && block.blocks.length > 0;

  return (
    <div className={`block-item ${block.type}`} data-block-id={block.block_id}>
      {renderContent()}
      <SyncStatusIndicator status={block.syncStatus} />
      
      {/* Sub-blocks indicator */}
      {hasSubBlocks && (
        <div 
          className="block-subblocks-indicator" 
          title={`Has ${block.blocks?.length} sub-block${block.blocks?.length !== 1 ? 's' : ''}`}
        >
          <span className="subblocks-count">{block.blocks?.length}</span>
        </div>
      )}
      
      {/* Insert Block Button (shown only in full block mode when editing) */}
      {fullBlockMode && isEditing && onInsertBlockBelow && (
        <button
          className="block-insert-btn"
          onClick={() => onInsertBlockBelow(block.block_id)}
          title="Insert block below"
        >
          + Insert Block
        </button>
      )}
      
      {/* Menu trigger border */}
      <div 
        className="block-menu-trigger" 
        onClick={handleMenuToggle}
        title="Block options"
      />
      
      {/* Overlay menu */}
      {showMenu && (
        <div className="block-menu-overlay" ref={menuRef}>
          <div className="block-menu">
            <button 
              className="block-menu-item"
              onClick={handleNavigateToBlock}
              title="Open block page"
            >
              <span className="menu-icon">→</span>
              <span className="menu-text">Open Block</span>
            </button>
            {onTranslateBlock && block.type !== 'image' && (
              <button 
                className="block-menu-item"
                onClick={handleTranslateBlock}
                title="Translate block"
              >
                <span className="menu-icon">🌐</span>
                <span className="menu-text">Translate Block</span>
              </button>
            )}
            {onDeleteBlock && (
              <button 
                className="block-menu-item"
                onClick={handleDeleteBlock}
                title="Delete block"
              >
                <span className="menu-icon">🗑️</span>
                <span className="menu-text">Delete Block</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockItem;
